import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeySquare, Copy, Mail, Power, Ban, RotateCcw, Clock, History } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, StatusPill, inputCls } from "@/components/Peep";

const DURATIONS = [["24", "24 hours"], ["48", "48 hours"], ["72", "72 hours"], ["168", "7 days"], ["336", "14 days"], ["custom", "Custom date & time"]];
const STATE_PILL = { active: "active", pending: "pending", disabled: "suspended", expired: "expired", revoked: "revoked" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const toLocalInput = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

function ScopeEditor({ value, onChange, creators }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const box = (k, label) => (
    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!value[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`scope-${k}`} className="accent-[#a03cff]" /> {label}</label>
  );
  return (
    <div className="grid gap-2 rounded-xl border border-white/10 p-3">
      <p className="text-xs font-semibold text-[#c77dff]">What the reviewer can inspect</p>
      <select value={value.creators} onChange={(e) => set("creators", e.target.value)} data-testid="scope-creators" className={inputCls}>
        <option value="all">All approved, published creators</option>
        <option value="selected">Selected creators only</option>
      </select>
      {value.creators === "selected" && (
        <div className="flex flex-wrap gap-2">
          {creators.length === 0 && <span className="text-xs text-[#a7a0af]">No active creators yet.</span>}
          {creators.map((c) => (
            <label key={c.id} className="flex items-center gap-1 text-xs"><input type="checkbox" className="accent-[#a03cff]" checked={value.creator_ids.includes(c.id)}
              onChange={(e) => set("creator_ids", e.target.checked ? [...value.creator_ids, c.id] : value.creator_ids.filter((x) => x !== c.id))} /> {c.display_name}</label>
          ))}
        </div>
      )}
      <p className="text-[0.65rem] text-[#6f6878]">Public previews are always included. Unpublished drafts are never visible.</p>
      {box("premium", "Published premium (members-only) content")}
      {box("ppv", "Published pay-per-view content")}
      {box("live", "Authorized livestreams (view only)")}
    </div>
  );
}

const DEFAULT_SCOPE = { creators: "all", creator_ids: [], premium: true, ppv: false, live: true };

function CreateReviewer({ creators, onDone }) {
  const [f, setF] = useState({ name: "", organization: "CCBill", email: "", username: "", duration: "72", expires: "", starts: "" });
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name: f.name, organization: f.organization, email: f.email.trim(), username: f.username.trim().toLowerCase(), scope,
        starts_at: f.starts ? new Date(f.starts).toISOString() : null,
        ...(f.duration === "custom" ? { expires_at: f.expires ? new Date(f.expires).toISOString() : null } : { duration_hours: Number(f.duration) }) };
      await api.post("/admin/reviewers", body);
      toast.success("Reviewer created — it stays Pending until you activate it");
      setF({ ...f, name: "", email: "", username: "" });
      onDone();
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid gap-2 md:grid-cols-2" noValidate data-testid="reviewer-create-form">
      <input value={f.name} onChange={set("name")} placeholder="Reviewer name" data-testid="rv-name" className={inputCls} />
      <input value={f.organization} onChange={set("organization")} placeholder="Organization" data-testid="rv-org" className={inputCls} />
      <input value={f.email} onChange={set("email")} type="email" autoComplete="off" placeholder="Reviewer email" data-testid="rv-email" className={inputCls} />
      <input value={f.username} onChange={set("username")} autoCapitalize="none" placeholder="Unique username (e.g. ccbill_review)" data-testid="rv-username" className={inputCls} />
      <label className="grid gap-1 text-xs text-[#a7a0af]">Access starts (blank = now)
        <input type="datetime-local" value={f.starts} onChange={set("starts")} data-testid="rv-starts" className={inputCls} /></label>
      <label className="grid gap-1 text-xs text-[#a7a0af]">Access duration
        <select value={f.duration} onChange={set("duration")} data-testid="rv-duration" className={inputCls}>{DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      {f.duration === "custom" && <label className="grid gap-1 text-xs text-[#a7a0af] md:col-span-2">Expires at
        <input type="datetime-local" value={f.expires} onChange={set("expires")} data-testid="rv-expires" className={inputCls} /></label>}
      <div className="md:col-span-2"><ScopeEditor value={scope} onChange={setScope} creators={creators} /></div>
      <button disabled={busy} data-testid="rv-create" className="btn-brand rounded-xl py-3 text-sm font-semibold md:col-span-2">{busy ? "Creating…" : "Create reviewer account"}</button>
    </form>
  );
}

function ReviewerCard({ r, creators, reload }) {
  const [link, setLink] = useState("");
  const [extend, setExtend] = useState("24");
  const [exp, setExp] = useState(toLocalInput(r.expires_at));
  const [scope, setScope] = useState(r.scope);
  const [log, setLog] = useState(null);
  const [open, setOpen] = useState(false);
  const act = async (a, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/admin/reviewers/${r.id}/${a}`); toast.success(`Reviewer ${a === "rotate" ? "credentials rotated — send a new invitation" : a + "d"}`); reload(); } catch (e) { err(e); }
  };
  const update = async (body, msg) => { try { await api.put(`/admin/reviewers/${r.id}`, body); toast.success(msg); reload(); } catch (e) { err(e); } };
  const invite = async (send) => {
    try {
      const { data } = await api.post(`/admin/reviewers/${r.id}/invite`, { send_email: send });
      setLink(data.link);
      if (send) (data.emailed ? toast.success("Invitation emailed") : toast.error(data.email_error || "Email not sent — copy the link instead"));
      else toast.success("Single-use setup link generated");
      reload();
    } catch (e) { err(e); }
  };
  const loadLog = async () => { setOpen(!open); if (!log) { const { data } = await api.get(`/admin/reviewers/${r.id}/log`); setLog(data.log); } };
  const revoked = r.state === "revoked";
  return (
    <div data-testid={`reviewer-${r.username}`} className="rounded-2xl border border-white/10 bg-[#0c0a10] p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{r.name} <span className="text-xs text-[#a7a0af]">· {r.organization}</span></p>
          <p className="text-xs text-[#a7a0af]">@{r.username} · {r.email}</p>
        </div>
        <span data-testid={`reviewer-state-${r.username}`}><StatusPill status={STATE_PILL[r.state] === "suspended" ? "disabled" : r.state} /></span>
      </div>
      <div className="mt-3 grid gap-1 text-xs text-[#c7c1cf] sm:grid-cols-2">
        <span>Starts: {fmt(r.starts_at)}</span>
        <span data-testid={`reviewer-expires-${r.username}`}>Expires: {fmt(r.expires_at)}</span>
        <span>Password set: {r.password_set ? fmt(r.password_set_at) : "Not yet"}</span>
        <span>Last login: {fmt(r.last_login_at)}</span>
        <span>Last activity: {fmt(r.last_activity_at)}</span>
        <span>Setup link: {r.invite_pending_until ? `valid until ${fmt(r.invite_pending_until)}` : "none active"}</span>
      </div>
      {!revoked && (
        <>
          <div className="mt-3 flex flex-wrap gap-2">
            {r.status !== "active" && <button onClick={() => act("activate")} data-testid={`rv-activate-${r.username}`} className="btn-brand flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold"><Power size={12} /> {r.status === "disabled" ? "Reactivate" : "Activate"}</button>}
            {r.status === "active" && <button onClick={() => act("disable")} data-testid={`rv-disable-${r.username}`} className="flex items-center gap-1 rounded-full border border-amber-400/50 px-3 py-1.5 text-xs text-amber-200"><Power size={12} /> Disable</button>}
            <button onClick={() => invite(false)} data-testid={`rv-link-${r.username}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs"><Copy size={12} /> Generate setup link</button>
            <button onClick={() => invite(true)} data-testid={`rv-email-${r.username}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs"><Mail size={12} /> Email invitation</button>
            <button onClick={() => act("rotate", "Rotate credentials? The current password and sessions stop working immediately.")} data-testid={`rv-rotate-${r.username}`} className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-xs"><RotateCcw size={12} /> Rotate credentials</button>
            <button onClick={() => act("expire", "End this reviewer's access right now?")} data-testid={`rv-expire-${r.username}`} className="flex items-center gap-1 rounded-full border border-amber-400/50 px-3 py-1.5 text-xs text-amber-200"><Clock size={12} /> Expire now</button>
            <button onClick={() => act("revoke", "Revoke permanently? This cannot be undone.")} data-testid={`rv-revoke-${r.username}`} className="flex items-center gap-1 rounded-full border border-red-400/50 px-3 py-1.5 text-xs text-red-300"><Ban size={12} /> Revoke</button>
          </div>
          {link && (
            <div className="mt-3 flex gap-2">
              <input readOnly value={link} data-testid={`rv-link-value-${r.username}`} className={`${inputCls} flex-1 text-xs`} />
              <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied — share it privately"); }} className="rounded-xl border border-white/15 px-3 text-xs">Copy</button>
            </div>
          )}
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <div className="flex gap-2">
              <select value={extend} onChange={(e) => setExtend(e.target.value)} data-testid={`rv-extend-select-${r.username}`} className={inputCls}>{DURATIONS.slice(0, 5).map(([v, l]) => <option key={v} value={v}>+{l}</option>)}</select>
              <button onClick={() => update({ extend_hours: Number(extend) }, "Access extended")} data-testid={`rv-extend-${r.username}`} className="flex items-center gap-1 rounded-xl border border-white/15 px-3 text-xs"><Clock size={12} /> Extend</button>
            </div>
            <div className="flex gap-2">
              <input type="datetime-local" value={exp} onChange={(e) => setExp(e.target.value)} data-testid={`rv-exp-input-${r.username}`} className={inputCls} />
              <button onClick={() => update({ expires_at: new Date(exp).toISOString() }, "Expiration updated")} data-testid={`rv-exp-save-${r.username}`} className="rounded-xl border border-white/15 px-3 text-xs">Set</button>
            </div>
          </div>
          <div className="mt-3"><ScopeEditor value={scope} onChange={setScope} creators={creators} /></div>
          <button onClick={() => update({ scope }, "Permissions updated — effective immediately")} data-testid={`rv-scope-save-${r.username}`} className="mt-2 rounded-xl border border-[#a03cff] px-4 py-2 text-xs">Save permissions</button>
        </>
      )}
      <button onClick={loadLog} data-testid={`rv-log-${r.username}`} className="mt-3 flex items-center gap-1 text-xs text-[#c77dff]"><History size={12} /> {open ? "Hide" : "Show"} access history</button>
      {open && log && (
        <ul className="mt-2 max-h-56 overflow-y-auto text-xs" data-testid={`rv-log-list-${r.username}`}>
          {log.length === 0 && <li className="text-[#a7a0af]">No events yet.</li>}
          {log.map((l) => <li key={l.id} className="flex justify-between gap-2 border-b border-white/5 py-1"><span>{l.action.replace(/_/g, " ")} {l.detail && <span className="text-[#a7a0af]">· {l.detail}</span>}</span><span className="shrink-0 text-[#6f6878]">{fmt(l.created_at)}</span></li>)}
        </ul>
      )}
    </div>
  );
}

export default function AdminDemoAccess() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/reviewers").then((r) => setD(r.data)).catch(err), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  return (
    <div data-testid="demo-access-page" className="flex flex-col gap-5">
      <h1 className="flex items-center gap-2 text-2xl font-bold"><KeySquare className="text-[#d42be8]" /> Demo Access Management</h1>
      <p className="max-w-3xl text-sm text-[#a7a0af]">Temporary, complimentary premium reviewer accounts for payment processors, partners, auditors and other authorized third parties. Reviewers use the normal subscriber app, read-only, with no payments, admin, creator finance, banking or private messages. Access is enforced on the server on every request and never creates subscriptions or revenue.</p>
      <Panel title="Review environment">
        <p className="text-sm">Reviewers sign in at: <span data-testid="review-url" className="font-mono text-[#e0c2ff]">{d.review_url}</span></p>
        <p className="mt-1 text-xs text-[#a7a0af]">Content available right now: {d.active_creators} active creators · {d.published_content} published items. {d.published_content === 0 && "With no published creator content, a reviewer will only see the branded dashboard and Coming Soon messages."}</p>
      </Panel>
      <Panel title="Create reviewer"><CreateReviewer creators={d.creators} onDone={load} /></Panel>
      <Panel title={`Reviewers (${d.reviewers.length})`}>
        <div className="flex flex-col gap-3">
          {d.reviewers.length === 0 && <p data-testid="reviewers-empty" className="text-sm text-[#a7a0af]">No reviewer accounts yet.</p>}
          {d.reviewers.map((r) => <ReviewerCard key={`${r.id}-${r.status}-${r.expires_at}`} r={r} creators={d.creators} reload={load} />)}
        </div>
      </Panel>
    </div>
  );
}
